import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { IoIosArrowBack } from 'react-icons/io';
import ProductImagesAndDetails from './ProductImagesAndDetails';
import ProductImagesAndDetailsMobile from './ProductImagesAndDetailsMobile'; // Mobile 컴포넌트 import
import { instance } from '../../api/axiosInstance';

// `formatDate` 함수 컴포넌트 외부로 이동
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return '';
  const date = new Date(dateString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // 월은 0부터 시작하므로 +1 해줌
  const day = String(date.getDate()).padStart(2, '0'); // 날짜를 두 자리로 맞추기

  return `${year}/${month}/${day}`;
};

export default function ProductsDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [product, setProduct] = useState<any>(null);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        const response = await instance.get(`/api/products/${id}`);
        setProduct(response.data);
        setLoading(false);
      } catch (err) {
        setError('상품을 가져오는 데 실패했습니다.');
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [id]);

  const allImages = useMemo(() => {
    const images = [
      product?.image,
      ...(product?.productImages?.map(
        (img: { imageUrl: string }) => img.imageUrl
      ) || []),
    ].filter((image): image is string => !!image); // null이나 undefined를 제거

    // 중복 제거
    return images.filter((image, index) => images.indexOf(image) === index);
  }, [product]);

  useEffect(() => {
    if (allImages.length > 0) {
      setSelectedImage(allImages[0]);
    }
  }, [allImages]);

  const formattedDate = useMemo(
    () => formatDate(product?.date),
    [product?.date]
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
  
    // 초기화 및 이벤트 리스너 추가
    handleResize();
    window.addEventListener('resize', handleResize);
  
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const modelingModelUrl = useMemo(() => {
    if (product?.category === 'modeling' && Array.isArray(product?.models)) {
      return product.models
        .map((model: { modelUrl: string }) => model.modelUrl)
        .join(',');
    }
    return '';
  }, [product]);

  if (loading) {
    return <div>로딩 중...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!product) {
    return <div>상품을 찾을 수 없습니다.</div>;
  }

  return (
    <section className="max-w-[2560px] w-full mx-auto mt-[100px] mobile:px-4">
      <div>
        <article className="mobile:mt-[120px] max-w-[1440px] w-full mx-auto p-[30px] mobile:p-0 relative">
          <div onClick={() => navigate(-1)} className="bg-white cursor-pointer w-[40px] h-[40px] text-xl mobile:text-sm mobile:w-[32px] mobile:h-[32px] border border-gray-200 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center">
            <IoIosArrowBack
              
              className="text-[#4d6786] cursor-pointer"
            />
          </div>

          {isMobile ? (
            <ProductImagesAndDetailsMobile
              product={product}
              selectedImage={selectedImage}
              setSelectedImage={setSelectedImage}
              allImages={allImages}
              formattedDate={formattedDate}
              modelingModelUrl={modelingModelUrl}
            />
          ) : (
            <ProductImagesAndDetails
              product={product}
              selectedImage={selectedImage}
              setSelectedImage={setSelectedImage}
              allImages={allImages}
              formattedDate={formattedDate}
              modelingModelUrl={modelingModelUrl}
            />
          )}
        </article>
      </div>
    </section>
  );
}
